/**
 * UpgradeFeatureGateModal
 *
 * Global modal that listens for `valpha:feature-gate` events emitted by the
 * API client when the backend denies a request with HTTP 402.
 *
 * Payload (from subscriptions decorator):
 *   { feature: string, reason: string, remaining?: number, limit?: number, window_end?: string }
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Crown, AlertTriangle } from "lucide-react";

const FEATURE_LABELS = {
  ai_chat_message: "AI chat messages",
  backtest_run: "backtest runs",
  trade_execute: "trade executions",
  account_add: "broker account slots",
};

function humanReason(reason) {
  switch (reason) {
    case "quota_exceeded":
      return "You've reached your plan's limit for this feature in the current window.";
    case "feature_off":
      return "This feature is not included in your current plan.";
    case "no_subscription":
      return "Your subscription is no longer active.";
    case "trial_expired":
      return "Your free trial has ended.";
    default:
      return "This action requires a higher plan.";
  }
}

export default function UpgradeFeatureGateModal() {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    const onGate = (ev) => {
      // Coalesce: don't replace an existing visible modal mid-interaction.
      setEvent((prev) => prev || ev?.detail || {});
    };
    window.addEventListener("valpha:feature-gate", onGate);
    return () => window.removeEventListener("valpha:feature-gate", onGate);
  }, []);

  if (!event) return null;

  const close = () => setEvent(null);
  const featureLabel = FEATURE_LABELS[event.feature] || event.feature || "this feature";
  const reasonText = humanReason(event.reason);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={close} />
      <div
        role="dialog"
        aria-labelledby="upgrade-gate-title"
        className="relative z-10 w-full max-w-md rounded-xl border border-borderDefault bg-backgroundSurface shadow-2xl p-6 space-y-4"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 rounded-md p-1 text-textSecondary hover:bg-white/10 hover:text-textPrimary"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          </div>
          <div className="min-w-0">
            <h3 id="upgrade-gate-title" className="text-base font-semibold text-textPrimary">
              Upgrade to continue
            </h3>
            <p className="text-sm text-textSecondary mt-1">
              {reasonText} You've hit your limit for{" "}
              <span className="font-medium text-textPrimary">{featureLabel}</span>.
            </p>
          </div>
        </div>

        {(event.limit !== undefined && event.limit !== null) && (
          <div className="rounded-md border border-borderDefault bg-backgroundBody/50 p-3 text-xs text-textSecondary">
            Plan limit: <span className="text-textPrimary font-medium">{event.limit}</span>
            {event.window_end ? (
              <>
                {" "}
                · resets {new Date(event.window_end).toLocaleString(undefined, {
                  month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                })}
              </>
            ) : null}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={close}
            className="rounded-md border border-borderDefault px-3 py-1.5 text-sm text-textSecondary hover:bg-white/5"
          >
            Not now
          </button>
          <Link
            to="/subscription"
            onClick={close}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#4FA9A1] hover:bg-[#3e938b] text-[#0F1826] px-3 py-1.5 text-sm font-semibold"
          >
            <Crown className="w-4 h-4" />
            View plans
          </Link>
        </div>
      </div>
    </div>
  );
}
